import { originalCatalog } from "@marshmallow-duel/game-data";
import { abilityById } from "@marshmallow-duel/simulation";

import type { MatchState, PlayerState, ProjectileKind, WorldObjectDefinition } from "@marshmallow-duel/simulation";

const projectileSprites: Readonly<Partial<Record<ProjectileKind, string>>> = {
  mine: "spr_usedability_mine",
  clusterMine: "spr_usedability_clustermine",
  grenade: "spr_usedability_point",
  clusterGrenade: "spr_usedability_clusternade",
  flashGrenade: "spr_usedability_flashnade",
  fireball: "spr_usedability_fireball",
  freezer: "spr_usedability_freezer",
  teleporter: "spr_usedability_teleporter",
  hover: "spr_usedability_hover",
  bomb: "spr_usedability_bomb",
  bomber: "spr_usedability_bomber",
  rocket: "spr_usedability_rocket",
  destroyer: "spr_usedability_destroyer",
  runBomb: "spr_usedability_runbomb",
  weasel: "spr_usedability_weasel",
  net: "spr_usedability_net",
  flame: "spr_usedability_point",
  pistol: "spr_usedability_pistol",
  laser: "spr_usedability_laser"
};

export interface RenderOptions {
  readonly oldTextures?: boolean;
  readonly raining?: boolean;
  readonly showFps?: boolean;
  readonly fps?: number;
}

const gmColor = (color: number): string => {
  const red = color & 0xff;
  const green = (color >> 8) & 0xff;
  const blue = (color >> 16) & 0xff;
  return `rgb(${red} ${green} ${blue})`;
};

class ImageStore {
  readonly #images = new Map<string, HTMLImageElement>();

  get(url: string): HTMLImageElement | null {
    const cached = this.#images.get(url);
    if (cached) return cached.complete ? cached : null;
    const image = new Image();
    image.src = url;
    image.decoding = "async";
    this.#images.set(url, image);
    return null;
  }
}

const images = new ImageStore();
const tintCanvas = document.createElement("canvas");
const tintContext = tintCanvas.getContext("2d", { alpha: true });

const spriteForPlayer = (player: PlayerState): string => {
  const requested = `spr_player_${player.profile.skin}_${player.pose}`;
  return originalCatalog.sprites[requested] ? requested : `spr_player_1_${player.pose}`;
};

const drawSprite = (
  context: CanvasRenderingContext2D,
  spriteName: string,
  frame: number,
  x: number,
  y: number,
  scaleX = 1,
  tint: string | null = null,
  alpha = 1
): void => {
  const sprite = originalCatalog.sprites[spriteName];
  if (!sprite?.frames.length) return;
  const selected = sprite.frames[Math.abs(Math.floor(frame)) % sprite.frames.length];
  if (!selected) return;
  const image = images.get(selected.url);
  if (!image) return;
  const drawX = scaleX === 1 ? x - sprite.origin.x : x - (selected.width - sprite.origin.x);
  const drawY = y - sprite.origin.y;
  context.save();
  context.globalAlpha = alpha;
  if (scaleX === -1) {
    context.translate(x * 2, 0);
    context.scale(-1, 1);
  }
  if (!tint || !tintContext) {
    context.drawImage(image, drawX, drawY);
  } else {
    tintCanvas.width = selected.width;
    tintCanvas.height = selected.height;
    tintContext.clearRect(0, 0, selected.width, selected.height);
    tintContext.drawImage(image, 0, 0);
    tintContext.globalCompositeOperation = "multiply";
    tintContext.fillStyle = tint;
    tintContext.fillRect(0, 0, selected.width, selected.height);
    tintContext.globalCompositeOperation = "destination-in";
    tintContext.drawImage(image, 0, 0);
    tintContext.globalCompositeOperation = "source-over";
    context.drawImage(tintCanvas, drawX, drawY);
  }
  context.restore();
};

const drawWorldObject = (context: CanvasRenderingContext2D, object: WorldObjectDefinition): void => {
  const sprite = object.sprite ?? originalCatalog.objects[object.sourceObject]?.sprite;
  if (sprite) drawSprite(context, sprite, 0, object.x, object.y);
};

const accessorySprite = (player: PlayerState, suffix: string): string | null => {
  const pose = player.pose === "rope" ? "rope" : player.pose;
  const name = `spr_player_${player.profile.skin}_${pose}_ability_${suffix}`;
  return originalCatalog.sprites[name] ? name : null;
};

const drawPlayer = (context: CanvasRenderingContext2D, player: PlayerState): void => {
  if (!player.alive) return;
  const alpha = player.invisible ? 0.08 : 1;
  const color = player.frozenTicks > 0 ? "rgb(190 251 250)" : gmColor(player.profile.color);
  drawSprite(context, spriteForPlayer(player), player.frame, player.x, player.y, player.facing, color, alpha);
  const overlay = player.abilityId === 1
    ? accessorySprite(player, "boots")
    : player.abilityId === 11
      ? accessorySprite(player, "flash")
      : player.pose === "pistol"
        ? accessorySprite(player, "pistol")
        : player.netTicks > 0
          ? accessorySprite(player, "net")
          : player.pose === "magnet"
            ? accessorySprite(player, "magnet")
            : player.pose === "parachute"
              ? accessorySprite(player, "parachute")
              : null;
  if (overlay) drawSprite(context, overlay, player.frame, player.x, player.y, player.facing, player.abilityId === 1 ? "rgb(185 81 51)" : null, alpha);
};

const drawWater = (context: CanvasRenderingContext2D, tick: number): void => {
  const waterFrame = Math.floor(tick / 2) % 16;
  for (let x = 0; x < 800; x += 32) {
    drawSprite(context, "spr_water", waterFrame, x, 544);
    drawSprite(context, "spr_deepwater", 0, x, 576);
  }
};

const drawRain = (context: CanvasRenderingContext2D, tick: number): void => {
  context.save();
  context.strokeStyle = "rgba(190, 215, 255, 0.45)";
  context.lineWidth = 1;
  for (let index = 0; index < 72; index += 1) {
    const x = (index * 149 + tick * 9) % 832 - 16;
    const y = (index * 83 + tick * 17) % 632 - 16;
    context.beginPath();
    context.moveTo(x, y);
    context.lineTo(x - 4, y + 12);
    context.stroke();
  }
  context.restore();
};

const drawAbilityPreviews = (context: CanvasRenderingContext2D, state: MatchState): void => {
  if (!state.rules.fighterAbility) return;
  const positions: Readonly<Record<number, readonly [number, number]>> = {
    1: [32, 15],
    2: [768, 15],
    3: [32, 573],
    4: [768, 573]
  };
  for (const player of state.players) {
    if (!player.alive || player.abilityId <= 0) continue;
    const abilityId = player.abilityId === 100 ? 10 : player.abilityId === 101 ? 33 : player.abilityId;
    const icon = abilityById(abilityId)?.icon;
    const position = positions[player.slot];
    if (icon && position) drawSprite(context, icon, 0, position[0], position[1]);
  }
};

export const renderMatch = (canvas: HTMLCanvasElement, state: MatchState, options: RenderOptions = {}): void => {
  const context = canvas.getContext("2d", { alpha: false });
  if (!context) return;
  context.imageSmoothingEnabled = false;
  context.fillStyle = "#000";
  context.fillRect(0, 0, 800, 600);

  drawSprite(context, options.oldTextures ? "spr_abilityemitter_old" : "spr_abilityemitter", 0, state.abilityEmitterX, 0);

  const objects = [
    ...state.map.objects.filter(({ id }) => !state.destroyedObjectIds.includes(id)),
    ...state.temporaryWalls
  ].sort((left, right) => (originalCatalog.objects[right.sourceObject]?.depth ?? 0) - (originalCatalog.objects[left.sourceObject]?.depth ?? 0));
  for (const object of objects) drawWorldObject(context, object);

  for (const rope of state.ropes) {
    const owner = state.players.find(({ slot }) => slot === rope.owner);
    if (!owner) continue;
    context.strokeStyle = "#ece6c8";
    context.lineWidth = 2;
    context.beginPath();
    context.moveTo(owner.x, owner.y);
    context.lineTo(rope.anchorX, rope.anchorY);
    context.stroke();
    drawSprite(context, options.oldTextures ? "spr_ropeend_old" : "spr_ropeend", 0, rope.anchorX, rope.anchorY);
  }

  for (const pickup of state.pickups) {
    drawSprite(context, options.oldTextures ? "spr_abilitybox_old" : "spr_abilitybox", state.tick / 4, pickup.x, pickup.y);
    const icon = abilityById(pickup.abilityId)?.icon;
    if (icon) drawSprite(context, icon, 0, pickup.x, pickup.y);
  }

  for (const projectile of state.projectiles) {
    const sprite = projectile.kind === "kickWave"
      ? Math.abs(projectile.vx) >= Math.abs(projectile.vy) ? "spr_world_kick_x" : "spr_world_kick_y"
      : projectileSprites[projectile.kind];
    if (sprite) drawSprite(context, sprite, state.tick / 3, projectile.x, projectile.y, projectile.vx < 0 ? -1 : 1);
  }

  drawWater(context, state.tick);
  if (options.raining) drawRain(context, state.tick);
  for (const player of state.players) drawPlayer(context, player);

  context.font = "bold 11px Tahoma, sans-serif";
  context.fillStyle = "#fff";
  context.textBaseline = "top";
  if (state.rules.showScore) {
    context.fillText(String(state.scores[1]), 5, 5);
    const score2 = String(state.scores[2]);
    context.fillText(score2, 795 - context.measureText(score2).width, 5);
    if (state.rules.playerCount >= 3) context.fillText(String(state.scores[3]), 5, 578);
    if (state.rules.playerCount === 4) {
      const score4 = String(state.scores[4]);
      context.fillText(score4, 795 - context.measureText(score4).width, 578);
    }
  }
  drawAbilityPreviews(context, state);

  if (options.showFps && Number.isFinite(options.fps)) {
    const text = `${Math.round(options.fps ?? 0)} FPS`;
    context.fillText(text, 795 - context.measureText(text).width, 30);
  }

  if (state.flashTicks > 0) {
    context.fillStyle = `rgba(255,255,255,${state.flashTicks > 20 ? 1 : state.flashTicks / 20})`;
    context.fillRect(0, 0, 800, 600);
  }
};
